import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T, TF } from "react-translator-component";
import { Box, Typography } from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import { styles } from "../styles";
import ButtonArrow from "../../components/GreenCity/ButtonArrow";
import ButtonArrow2 from "../../components/GreenCity/ButtonArrow2";
import LoadingModal from "../../components/LoadingModal";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { setGroupInfo } from "../../redux/Reducers/gcGroupReducer";
import { isMemberRole, isFinanceRole, isSubLeaderRole, isGroupLeader } from "../../util/utils";
import appServices from "../../services/app-services/appServices";

const MemberList = () => {
  const defaultImage = require("../../assets/images/default.png");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const group = useSelector((state: any) => state.gcGroup);
  const { user } = useSelector((state: any) => state.userAuth);
  const [activeEvents, setActiveEvents] = useState([]);
  const [expiredEvents, setExpiredEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [cancelRequests, setCancelRequests] = useState<any>([]);

  const getEventsWithUsers = async () => {
    try {
      setLoading(true);
      const res = await appServices.getEventListByMemberGroup(group?.id);
      const events = res.post_events.filter(
        (event: any) => event.users?.length > 0
      );

      const filteredData = events?.map((event: any) => ({
        eventTitle: event.title,
        eventStartDate: event.start_date,
        eventEndDate: event.end_date, // Include end_date
        eventId: event.id,
        status: event.status,
      }));

      const today = dayjs().startOf("day"); // Midnight of today

      const expiredEvents = filteredData?.filter((event: any) => {
        const eventDate = dayjs(event.eventEndDate).startOf("day");
        return (
          eventDate.isBefore(today) ||
          event.status?.toLowerCase() === "cancelled"
        );
      });

      const activeEvents = filteredData?.filter((event: any) => {
        const eventDate = dayjs(event.eventEndDate).startOf("day");
        return (
          (eventDate.isSame(today, "day") || eventDate.isAfter(today)) &&
          event.status?.toLowerCase() !== "cancelled"
        );
      });

      // Update state or use the arrays as needed
      setExpiredEvents(expiredEvents); // Assuming you have a state for expired events
      setActiveEvents(activeEvents);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const getCancelRequests = async () => {
    try {
      const res = await appServices.getCancelRequestsOfGroup(group?.id);
      setCancelRequests(res?.post_event_users);
    } catch (error) {
      console.error(error);
    }
  };

  const handleMembers = () => {
    dispatch(setGroupInfo(group));
    navigate("/members");
  };

  const apiCall = async () => {
    const res = await appServices.getMemberPendingList(group.id);
    setMembers(res?.member_group_users);
    getCancelRequests()
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    getEventsWithUsers();
    apiCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const numberOfApprovedMember = () => {
    const approvedMember = group?.memberGroupUsers?.filter((user: any) => user.status === "approved")
    // memberGroupUsers内にリーダーがいないため、最低でも1人になる
    return approvedMember.length + 1 || 1
  }

  const handleNavigateApprovedMemberList = () => {
    navigate("/approved_member_list", {state: { groupId: group?.id }});
  }

  const handleNavigateEventUsers = (event: any) => {
    navigate("/eventusers", { state: { groupId: group?.id, event: event } });
  }

  const isExistsCanselRequests = (eventId: number) => {
    return cancelRequests.some((cancelRequest: any) => {
      if (cancelRequest.event_id === eventId) {
        return cancelRequest.status === "Cancel-pending"
      }
      return false
    })
  }

  return (
    <Box>
      <Box
        style={{
          paddingTop: 100,
          paddingLeft: 16,
          paddingBottom: "12px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
        }}
      >
        <LoadingModal visible={loading} handleClose={() => {}} />

        <GCBackButton onClick={() => navigate(-1)} />

        <Box style={style.eventPostingBox}>
          <Typography style={{ fontWeight: "700" }}>
            <img
              src={group?.groupImage instanceof File ? URL.createObjectURL(group.groupImage) : defaultImage}
              alt={"img"}
              style={{
                ...styles.farmProfileIcon,
                height: 50,
                width: 50,
                margin: 0,
              }}
            />
            &nbsp;{group?.groupName}
          </Typography>
        </Box>
      </Box>
      <div style={styles.portionMemberListBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          <GroupIcon /> {T("member_list")}
        </p>

        <ButtonArrow
          title={TF(T("member_list_approved_member_list"), numberOfApprovedMember())}
          onClick={() => handleNavigateApprovedMemberList()}
        />

        <ButtonArrow
          disabled={isMemberRole(group?.role) || isFinanceRole(group?.role)}
          title={TF(T("member_list_pending_member_list"), members.length || 0)}
          onClick={() => handleMembers()}
        />
      </div>

      <div style={styles.portionMemberListBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>
          {T("member_list_event_users")}
        </p>
        {activeEvents?.map((event: any, index: number) => (
          <ButtonArrow2
            key={index}
            title={event.eventTitle}
            desc={dayjs(event.eventStartDate).format("YYYY年M月D日(ddd)")}
            onClick={() => handleNavigateEventUsers(event)}
          />
        ))}
      </div>

      <div style={styles.portionMemberListBg}>
        <p style={{ fontSize: 14, fontWeight: 600 }}>{T("member_list_finished_event")}</p>
        {(isGroupLeader(group, user) ||
          isSubLeaderRole(group?.role) ||
          isFinanceRole(group?.role)) &&
          expiredEvents?.map((event: any, index: number) => (
            <ButtonArrow2
              key={index}
              title={event.eventTitle}
              desc={
                // event.status === "Cancelled"
                isExistsCanselRequests(event.eventId)
                  ? T("member_list_cancelled")
                  : dayjs(event.eventStartDate).format("YYYY年M月D日(ddd)")
              }
              onClick={() => handleNavigateEventUsers(event)}
              badge={isExistsCanselRequests(event.eventId) ? T('refund_pending') : ''}
            />
          ))}
      </div>
    </Box>
  );
};

const style = {
  eventPostingIcon: {
    background: "#FF3B00",
    borderRadius: "50%",
    padding: "3px 6px 3px 6px",
    color: "white",
  },
  eventPostingBox: {
    display: "flex",
    gap: "15px",
    alignItmes: "center",
    paddingTop: "10px",
    paddingLeft: "10px",
  },
  showMore: {
    color: "#A2A2A2",
    textAlign: "right",
    marginRight: "15px",
  },
};

export default MemberList;
